import { Menu, Typography } from 'antd';
import moment from 'moment';
import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { InView } from 'react-intersection-observer';

import { EmptyComponent, Loading, MarkDot } from 'componets/ui';
import { DropDownComponent } from 'componets/common';
import { ReadAllNews } from 'pages/Account/News/ReadAllNews';

import { getNewsTitles, getNewsTitlesMore } from 'features/news/actions';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { Paths } from 'utils/constants';
import { language } from 'utils';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as RingIcon } from 'assets/icons/ring.svg';

import styles from './index.module.scss';

const PAGE_SIZE = 10;

export const AccountLayoutNewsDropdown: FC<{ unReadCount?: number }> = ({ unReadCount = 0 }) => {
	const formatter = Intl.NumberFormat('en', { notation: 'compact' });
	return (
		<div>
			<DropDownComponent
				destroyPopupOnHide
				isTable
				overlayClassName={styles.root}
				dropdownRender={() => <MenuDropdown />}
				label={
					<div
						data-count={formatter.format(unReadCount)}
						className={classNames(styles.ring, {
							[styles.read]: unReadCount === 0,
						})}
					>
						<RingIcon />
					</div>
				}
			/>
		</div>
	);
};

const MenuDropdown = () => {
	const { newsAndInformationText } = useTranslate(['newsAndInformationText']);

	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [inView, setInView] = useState(false);

	const { titles, pagerTitles: pager } = useAppSelector((state) => state.news);

	const dispatch = useAppDispatch();

	useEffect(() => {
		if (!isLoadingMore && inView && pager.currentPage < pager.pageCount) {
			setIsLoadingMore(true);
			dispatch(
				getNewsTitlesMore({
					lang: language,
					params: { pageSize: PAGE_SIZE, offset: pager.offset + PAGE_SIZE },
				}),
			).finally(() => {
				setTimeout(() => {
					setIsLoadingMore(false);
				}, 1000);
			});
		}
	}, [dispatch, inView, isLoadingMore, pager.offset]);

	useEffect(() => {
		setIsLoading(true);
		dispatch(getNewsTitles({ lang: language, params: { pageSize: PAGE_SIZE } })).finally(() => {
			setIsLoading(false);
		});
	}, [dispatch, setIsLoading]);

	return (
		<Menu>
			<div className={styles.title}>
				<Menu.Item key="news">
					<Link to={Paths.NEWS} className={styles.link}>
						{newsAndInformationText}
					</Link>
				</Menu.Item>
				<div>
					<div
						className={styles.column}
						onClick={(e) => {
							e.preventDefault();
						}}
					>
						<Menu.Item className={styles.close} key="close">
							<CloseIcon />
						</Menu.Item>
					</div>

					<ReadAllNews />
				</div>
			</div>

			{isLoading ? (
				<Loading />
			) : (
				<div className={styles.items}>
					{titles.length === 0 ? (
						<EmptyComponent />
					) : (
						titles.map((item, i) => (
							<div key={item.id} className={styles.item}>
								<Typography.Text type="secondary" className={styles.date}>
									{moment(item.publishedAt).format('YYYY-MM-DD')}
								</Typography.Text>

								<div className={styles.row}>
									<Menu.Item
										key={item.id}
										className={classNames(styles.newsTitle, { [styles.bold]: !item.read })}
									>
										<Link
											to={Paths.PIECE_OF_NEWS(item.id)}
											className={styles.link}
											state={{ page: Math.trunc(i / 20 + 1) }}
										>
											{item.title}
										</Link>
									</Menu.Item>

									<MarkDot item={item} isDropdown />
								</div>
							</div>
						))
					)}

					<InView
						as="div"
						onChange={(inView) => {
							setInView(inView);
						}}
					>
						<div />
					</InView>
				</div>
			)}
		</Menu>
	);
};
